import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Shield, KeyRound, RefreshCw, Plus, Trash2, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EmptyState, CardSkeleton } from '../common/States';
import { api } from '../../services/api';
import { getErrorMessage } from '../../lib/utils';

const GRANT_STYLE = {
  full: 'bg-emerald-500/15 text-emerald-700',
  read: 'bg-slate-500/15 text-slate-600',
  none: 'bg-transparent text-muted-foreground/50',
};
const GRANT_LABEL = { full: 'read + write', read: 'read', none: '—' };

const Section = ({ title, subtitle, children, action }) => (
  <Card className="mb-4">
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div>
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle && <p className="mt-1 text-[13px] text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent className="p-0">{children}</CardContent>
  </Card>
);

const Head = ({ eyebrow, title, sub, action }) => (
  <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
    <div>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{eyebrow}</p>
      <h1 className="mt-1 text-2xl font-bold text-foreground">{title}</h1>
      {sub && <p className="mt-1 max-w-3xl text-[13px] text-muted-foreground">{sub}</p>}
    </div>
    {action}
  </div>
);

const Notice = ({ children, tone = 'acc' }) => (
  <div
    className={`mb-4 flex gap-3 rounded-lg border p-4 text-[13px] ${
      tone === 'warn'
        ? 'border-amber-500/40 bg-amber-500/10 text-amber-800'
        : 'border-primary/30 bg-primary/5 text-foreground'
    }`}
  >
    <Shield className="mt-0.5 h-4 w-4 shrink-0" />
    <div>{children}</div>
  </div>
);

/* ─────────────────────────────── directory ─────────────────────────────── */

const RolesDirectory = () => {
  const [data, setData] = useState(null);
  const [operators, setOperators] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/access-control/directory'),
      api.get('/api/access-control/platform-roles').catch(() => null),
    ])
      .then(([dir, ops]) => { setData(dir.data); setOperators(ops?.data || null); })
      .catch((e) => toast.error(getErrorMessage(e, 'Could not load roles')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CardSkeleton rows={6} />;
  if (!data) return <EmptyState title="Roles unavailable" />;

  const table = (rows) => (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="p-3 font-medium">Role</th>
            <th className="p-3 font-medium">Key</th>
            <th className="p-3 font-medium">Reach</th>
            <th className="p-3 font-medium">People</th>
            <th className="p-3 font-medium">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-0">
              <td className="p-3 font-medium text-foreground">{r.name}</td>
              <td className="p-3 font-mono text-xs text-muted-foreground">{r.key}</td>
              <td className="p-3">
                <Badge variant="outline">{r.reach}</Badge>
              </td>
              <td className="p-3 tabular-nums">{r.users}</td>
              <td className="p-3 text-muted-foreground">{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Head
        eyebrow="Access control · directory"
        title="Roles & permissions"
        sub="Every role in the product, what it reaches, and how many people hold it right now. What a role can do is changed on the permission matrix."
      />
      <Notice>
        <b>A role gains or loses access on the matrix, not here.</b> Operator roles are fixed in
        number; tenant roles can be added per tenant on the Hierarchy tab. Reading a tenant&apos;s
        records always requires a logged proxy session, whatever the role.
      </Notice>
      {operators && (
        <Section
          title="Platform operator roles"
          subtitle="These accounts see across tenants. What each one reaches is the platform matrix."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-3 font-medium">Role</th>
                  <th className="p-3 font-medium">Key</th>
                  <th className="p-3 font-medium">Reach</th>
                  <th className="p-3 font-medium">People</th>
                  <th className="p-3 font-medium">Modules granted</th>
                  <th className="p-3 font-medium">MFA</th>
                  <th className="p-3 font-medium">May proxy</th>
                </tr>
              </thead>
              <tbody>
                {operators.roles.map((r) => (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="p-3 font-medium text-foreground">
                      {r.name}
                      {r.is_current && <Badge className="ml-2 bg-primary/15 text-primary">you</Badge>}
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.key}</td>
                    <td className="p-3 text-muted-foreground">{r.reach}</td>
                    <td className="p-3 tabular-nums">{r.users}</td>
                    <td className="p-3 tabular-nums">{r.modules_granted} / {operators.modules.length}</td>
                    {/* "required" alone was a lie: the flag is policy, and sign-in
                        never checks it. Saying so here is the whole fix until
                        somebody builds the enforcement. */}
                    <td className="p-3">
                      {r.mfa_required ? (
                        r.mfa_enforced ? 'required' : (
                          <span className="text-amber-700" title={r.mfa_note || ''}>
                            required · not enforced
                          </span>
                        )
                      ) : 'optional'}
                    </td>
                    <td className="p-3">
                      {r.may_proxy
                        ? <Badge className="bg-amber-500/15 text-amber-700">yes</Badge>
                        : <span className="text-muted-foreground">no</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {!operators && (
        <Section title="Platform roles" subtitle="Operator side — these accounts can see across tenants">
          {table(data.platform_roles)}
        </Section>
      )}
      <Section title="Tenant roles" subtitle="Seeded into every tenant; counts are across the whole fleet">
        {table(data.tenant_roles)}
      </Section>
    </>
  );
};

/* ──────────────────────────────── matrix ───────────────────────────────── */

const PermissionMatrix = ({ tenantId = 1 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  const load = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/api/access-control/matrix?tenant_id=${id}`);
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load the matrix'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tenantId); }, [tenantId, load]);

  const toggle = async (module, role) => {
    const current = data.cells[module][role];
    const key = `${module}|${role}`;
    setSaving(key);
    try {
      await api.put('/api/access-control/matrix/cell', {
        tenant_id: Number(tenantId),
        role,
        module,
        granted: current === 'none',
      });
      await load(tenantId);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not change that grant'));
    } finally {
      setSaving('');
    }
  };

  return (
    <>
      <Head
        eyebrow="Access control · the actual grant"
        title="Permission matrix"
        sub="Modules down, roles across. This is the grant the engine reads: a role without a module never sees the menu entry, and a deep link into it is refused at the router and again at the data layer."
      />

      {data && (
        <Notice tone="warn">
          <b>Verbs are granted per role, not per module.</b> {data.verb_control} A cell says whether the
          role reaches the module at all — click it to change that. Modules outside this tenant&apos;s
          plan cannot be granted and are shown greyed.
        </Notice>
      )}

      {loading || !data ? (
        <CardSkeleton rows={8} />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium" style={{ minWidth: 190 }}>Module</th>
                      {data.roles.map((r) => (
                        <th key={r.key} className="p-3 font-medium">
                          <div>{r.name}</div>
                          <div className="font-mono text-[10px] font-normal text-muted-foreground">
                            {data.role_meta[r.key]?.verbs?.length || 0} verbs
                            {data.role_meta[r.key]?.has_override ? ' · override' : ''}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.modules.map((mod) => {
                      const inPlan = data.plan_modules.includes(mod);
                      return (
                        <tr key={mod} className="border-b last:border-0">
                          <td className={`p-3 font-mono text-xs ${inPlan ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                            {mod}
                            {!inPlan && <span className="ml-2 text-[10px]">not in plan</span>}
                          </td>
                          {data.roles.map((r) => {
                            const grant = data.cells[mod][r.key];
                            const key = `${mod}|${r.key}`;
                            return (
                              <td key={r.key} className="p-2">
                                <button
                                  type="button"
                                  disabled={!inPlan || saving === key}
                                  onClick={() => toggle(mod, r.key)}
                                  title={`${mod} · ${r.name} — click to ${grant === 'none' ? 'grant' : 'remove'}`}
                                  className={`w-full rounded px-2 py-1 text-[11px] transition-colors ${GRANT_STYLE[grant]} ${
                                    inPlan ? 'hover:ring-1 hover:ring-primary/40' : 'cursor-not-allowed'
                                  }`}
                                >
                                  {saving === key ? '…' : GRANT_LABEL[grant]}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4 max-w-2xl">
            <CardContent className="p-4">
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-muted-foreground">
{data.rules.map((r, i) => `rule ${i + 1}   ${r}`).join('\n')}
              </pre>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

/* ─────────────────────────────── policies ──────────────────────────────── */

const AccessPolicies = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/access-control/policies')
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(getErrorMessage(e, 'Could not load policies')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CardSkeleton rows={8} />;
  if (!data) return <EmptyState title="Policies unavailable" />;

  return (
    <>
      <Head
        eyebrow="Access control · policy"
        title="Access policies"
        sub="The rules that sit around a role. Every row says whether something actually enforces it — a policy screen that overstates itself is worse than no policy screen."
      />
      {data.sections.map((section) => (
        <Section key={section.key} title={section.title} subtitle={section.subtitle}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-3 font-medium" style={{ minWidth: 220 }}>Rule</th>
                  <th className="p-3 font-medium">Value</th>
                  <th className="p-3 font-medium">Enforced</th>
                  <th className="p-3 font-medium">Where</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <tr key={row.rule} className="border-b last:border-0">
                    <td className="p-3 font-medium text-foreground">{row.rule}</td>
                    <td className="p-3 text-muted-foreground">{row.value}</td>
                    <td className="p-3">
                      {row.enforced ? (
                        <Badge className="bg-emerald-500/15 text-emerald-700">
                          <Check className="mr-1 h-3 w-3" /> yes
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/15 text-amber-700">
                          <X className="mr-1 h-3 w-3" /> not yet
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-muted-foreground">{row.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ))}
    </>
  );
};

/* ──────────────────────────────── staff ────────────────────────────────── */

const PlatformStaff = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [issued, setIssued] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/api/access-control/staff');
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load platform users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data: d } = await api.post('/api/access-control/service-accounts', {
        name: form.name.trim(),
        purpose: form.purpose.trim(),
        scopes: form.scopes,
        rotation_days: Number(form.rotation_days) || 90,
      });
      setIssued(d);
      setForm(null);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not create the account'));
    }
  };

  const rotate = async (id) => {
    try {
      const { data: d } = await api.post(`/api/access-control/service-accounts/${id}/rotate`);
      setIssued(d);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not rotate'));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/api/access-control/service-accounts/${id}`);
      toast.success('Service account revoked');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not revoke'));
    }
  };

  const recordRun = async (review) => {
    try {
      const { data: d } = await api.post(`/api/access-control/access-reviews/${review.id}/runs`, {
        reviewed_count: 0,
        removed_count: 0,
        flagged_count: 0,
      });
      toast.success(`${d.name} recorded`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not record the review'));
    }
  };

  if (loading || !data) return <CardSkeleton rows={8} />;

  return (
    <>
      <Head
        eyebrow="Access control · people"
        title="Platform users"
        sub="Who — and what — can see across tenant boundaries. Small on purpose."
      />

      {issued && (
        <Notice tone="warn">
          <b>Copy this token now.</b> Only its hash is stored, so it cannot be shown again.
          <pre className="mt-2 overflow-x-auto rounded bg-background/70 p-2 font-mono text-[11px]">{issued.token}</pre>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setIssued(null)}>Done</Button>
        </Notice>
      )}

      <Section title="Operators" subtitle="Accounts holding a platform role">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-3 font-medium">Email</th>
                <th className="p-3 font-medium">Role</th>
                <th className="p-3 font-medium">MFA</th>
                <th className="p-3 font-medium">Last seen</th>
                <th className="p-3 font-medium">State</th>
              </tr>
            </thead>
            <tbody>
              {data.operators.map((o) => (
                <tr key={o.id} className="border-b last:border-0">
                  <td className="p-3 font-medium text-foreground">{o.email}</td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{o.role}</td>
                  <td className="p-3">
                    {o.mfa_enrolled
                      ? <Badge className="bg-emerald-500/15 text-emerald-700">enrolled</Badge>
                      : <Badge className="bg-amber-500/15 text-amber-700">not enrolled</Badge>}
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {o.last_seen_at ? new Date(o.last_seen_at).toLocaleString() : 'never'}
                  </td>
                  <td className="p-3">
                    {o.is_active
                      ? <Badge className="bg-emerald-500/15 text-emerald-700">active</Badge>
                      : <Badge variant="secondary">disabled</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Service accounts"
        subtitle="A machine identity is still an identity. The secret is shown once and stored only as a hash."
        action={
          <Button size="sm" onClick={() => setForm({ name: '', purpose: '', scopes: [], rotation_days: 90 })}>
            <Plus className="mr-1 h-4 w-4" /> New account
          </Button>
        }
      >
        {form && (
          <div className="border-b bg-muted/30 p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <Label className="text-[13px]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="svc-billing-sync" />
              </div>
              <div className="space-y-1 lg:col-span-2">
                <Label className="text-[13px]">Purpose</Label>
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Pushes invoices to the finance system" />
              </div>
              <div className="space-y-1">
                <Label className="text-[13px]">Rotate every (days)</Label>
                <Input type="number" min="1" max="365" value={form.rotation_days}
                  onChange={(e) => setForm({ ...form, rotation_days: e.target.value })} />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[13px]">Scopes</Label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.available_scopes.map((s) => {
                  const on = form.scopes.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        scopes: on ? form.scopes.filter((x) => x !== s) : [...form.scopes, s],
                      })}
                      className={`rounded-full border px-3 py-1 font-mono text-[11px] ${
                        on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={create} disabled={!form.name.trim() || !form.purpose.trim()}>Create</Button>
              <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {data.service_accounts.length === 0 ? (
          <div className="p-4">
            <EmptyState compact icon={KeyRound} title="No service accounts yet"
              description="Create one when a system needs to authenticate without a person behind it." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-3 font-medium">Account</th>
                  <th className="p-3 font-medium">Purpose</th>
                  <th className="p-3 font-medium">Scopes</th>
                  <th className="p-3 font-medium">Token</th>
                  <th className="p-3 font-medium">Rotation</th>
                  <th className="p-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {data.service_accounts.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="p-3 font-medium text-foreground">{a.name}</td>
                    <td className="p-3 text-muted-foreground">{a.purpose}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(a.scopes.length ? a.scopes : ['none']).map((s) => (
                          <Badge key={s} variant="outline" className="font-mono text-[10px]">{s}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{a.token_prefix}…</td>
                    <td className="p-3">
                      {a.rotation_overdue
                        ? <Badge className="bg-red-500/15 text-red-600">overdue</Badge>
                        : <span className="text-muted-foreground">every {a.rotation_days}d</span>}
                    </td>
                    <td className="p-3">
                      {a.is_active ? (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => rotate(a.id)}>
                            <RefreshCw className="mr-1 h-3 w-3" /> Rotate
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => revoke(a.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : <Badge variant="secondary">revoked</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Access reviews" subtitle="A review that never removes anybody is not a review — the removed count is kept for that reason.">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="p-3 font-medium">Review</th>
                <th className="p-3 font-medium">Cadence</th>
                <th className="p-3 font-medium">Last run</th>
                <th className="p-3 font-medium">Removed</th>
                <th className="p-3 font-medium">State</th>
                <th className="p-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {data.reviews.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-3">
                    <div className="font-medium text-foreground">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.description}</div>
                  </td>
                  <td className="p-3 text-muted-foreground">every {r.cadence_days}d</td>
                  <td className="p-3 text-muted-foreground">
                    {r.last_run_at ? `${new Date(r.last_run_at).toLocaleDateString()} · ${r.last_run_by}` : 'never'}
                  </td>
                  <td className="p-3 tabular-nums">{r.removed_count}</td>
                  <td className="p-3">
                    <Badge className={r.overdue ? 'bg-red-500/15 text-red-600' : 'bg-emerald-500/15 text-emerald-700'}>
                      {r.state}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => recordRun(r)}>Record run</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
};

/* ─────────────────────────── platform matrix ───────────────────────────── */

const PLATFORM_GRANT_STYLE = {
  full: 'bg-emerald-500/15 text-emerald-700',
  read: 'bg-slate-500/15 text-slate-600',
  proxy: 'bg-amber-500/15 text-amber-700',
  none: 'bg-transparent text-muted-foreground/50',
};
const PLATFORM_GRANT_LABEL = { full: 'read + write', read: 'read', proxy: 'proxy only', none: '—' };
const CYCLE = ['none', 'read', 'full', 'proxy'];

const PlatformMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/api/access-control/platform-matrix');
      setData(d);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load the platform matrix'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingFor = (role, module) =>
    (data?.pending || []).find((p) => p.role_key === role && p.module_key === module);

  const requestChange = async (role, module, current) => {
    const next = CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
    const key = `${role}|${module}`;
    setBusy(key);
    try {
      await api.post('/api/access-control/platform-matrix/cell', {
        role_key: role, module_key: module, grant: next,
      });
      toast.success('Change requested', {
        description: 'It needs a second administrator before it takes effect.',
      });
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not request that change'));
    } finally {
      setBusy('');
    }
  };

  const decide = async (id, approve) => {
    setBusy(`req-${id}`);
    try {
      await api.post(`/api/access-control/platform-approvals/${id}/decide`, { approve });
      toast.success(approve ? 'Approved and applied' : 'Rejected');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not record that decision'));
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <Head
        eyebrow="Access control · the actual grant"
        title="Platform permission matrix"
        sub="Modules down, operator roles across. This grid is the source — the sidebar is generated from it, so a role without a grant never sees the menu entry, and a deep link into it is refused at the router. Click a cell to cycle its grant."
      />

      <Notice tone="warn">
        <b>Tenant business data has no direct read, on purpose.</b> No operator role reads a
        tenant&apos;s records straight out of the database. The only route is a proxy session —
        time-capped, consent-gated and written to a log the operator cannot edit.
      </Notice>

      {loading || !data ? <CardSkeleton rows={8} /> : (
        <>
          {data.pending.length > 0 && (
            <Section
              title={`${data.pending.length} change${data.pending.length === 1 ? '' : 's'} awaiting a second administrator`}
              subtitle={`Nobody approves their own request. Unused requests expire after ${data.approval_window_hours} hours.`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="p-3 font-medium">Cell</th>
                      <th className="p-3 font-medium">Change</th>
                      <th className="p-3 font-medium">Asked by</th>
                      <th className="p-3 font-medium">Expires</th>
                      <th className="p-3 text-right font-medium">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.map((p) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="p-3 font-mono text-xs">{p.role_key} · {p.module_key}</td>
                        <td className="p-3">
                          {PLATFORM_GRANT_LABEL[p.current_grant]} → <b>{PLATFORM_GRANT_LABEL[p.requested_grant]}</b>
                        </td>
                        <td className="p-3 text-muted-foreground">{p.requested_by}</td>
                        <td className="p-3 text-muted-foreground">
                          {p.expires_at ? new Date(p.expires_at).toLocaleString() : '—'}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="outline" disabled={busy === `req-${p.id}`}
                            onClick={() => decide(p.id, true)}>
                            <Check className="mr-1 h-3 w-3" />Approve
                          </Button>
                          <Button size="sm" variant="outline" className="ml-1" disabled={busy === `req-${p.id}`}
                            onClick={() => decide(p.id, false)}>
                            <X className="mr-1 h-3 w-3" />Reject
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-3 font-medium" style={{ minWidth: 200 }}>Module</th>
                      {data.roles.map((r) => (
                        <th key={r.key} className="p-3 font-medium">
                          <div>{r.name.replace('Platform ', '')}</div>
                          <div className="font-mono text-[10px] font-normal text-muted-foreground">{r.key}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.modules.map((mod) => (
                      <tr key={mod.key} className="border-b last:border-0">
                        <td className="p-3">
                          <div className="font-medium text-foreground">{mod.name}</div>
                          <div className="text-[11px] text-muted-foreground">{mod.description}</div>
                        </td>
                        {data.roles.map((r) => {
                          const grant = data.grants[r.key]?.[mod.key] || 'none';
                          const key = `${r.key}|${mod.key}`;
                          const pending = pendingFor(r.key, mod.key);
                          return (
                            <td key={r.key} className="p-2">
                              <button
                                type="button"
                                disabled={busy === key || Boolean(pending)}
                                onClick={() => requestChange(r.key, mod.key, grant)}
                                title={pending
                                  ? `Awaiting approval: ${pending.current_grant} → ${pending.requested_grant}`
                                  : `${mod.name} · ${r.name} — click to cycle`}
                                className={`w-full rounded px-2 py-1 text-[11px] transition-colors ${PLATFORM_GRANT_STYLE[grant]} ${
                                  pending ? 'cursor-not-allowed ring-1 ring-amber-500/50' : 'hover:ring-1 hover:ring-primary/40'
                                }`}
                              >
                                {busy === key ? '…' : pending ? `→ ${PLATFORM_GRANT_LABEL[pending.requested_grant]}` : PLATFORM_GRANT_LABEL[grant]}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4 max-w-2xl">
            <CardContent className="p-4">
              <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-muted-foreground">
{`rule 1   a grant is checked at the router, before any handler runs
rule 2   removing a grant takes effect on the next request, not the next sign-in
rule 3   no cell can be changed by one person alone
rule 4   the request reaches the audit trail before the permission moves`}
              </pre>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

const PlatformAccessScreens = ({ view = 'directory', tenantId = 1 }) => {
  if (view === 'platform-matrix') return <PlatformMatrix />;
  if (view === 'matrix') return <PermissionMatrix tenantId={tenantId} />;
  if (view === 'policies') return <AccessPolicies />;
  if (view === 'staff') return <PlatformStaff />;
  return <RolesDirectory />;
};

export default PlatformAccessScreens;
